import { api } from '../../../config';
import {
  usesDyedUndyedAlpha,
  requiresLpgCargoRatioForCargo,
  isLpgProductId,
} from '../../../shared/offer-domain/constants';
import { enrichLpgDeliveryItemsWithCargoRatio } from '../../../shared/offer-domain/delivery';
import {
  getListData,
  mapDeliveryDetailRow,
  enrichDeliveryItemsWithFobLocations,
  enrichDeliveryItemsWithCounterOffers,
  expandFobOriginCapableRows,
  hasCounterOffer,
} from '../components/ModelDelivery/shared/utils';

export const CARGO_ALPHA_REQUIRED_MESSAGE =
  'Offer at least one cargo price (Alpha) on a capable option, or fill Alpha on at least one counter cargo, before submitting.';

export const CARGO_CAPABLE_ALPHA_REQUIRED_MESSAGE =
  'Fill Alpha pricing for every option marked Is Capable before submitting.';

export const CARGO_COUNTER_ALPHA_REQUIRED_MESSAGE =
  'Fill Alpha pricing for every counter cargo before submitting.';

export const isPricingValueFilled = value =>
  value !== '' && value !== null && value !== undefined;

const isCapableActive = row =>
  row?.isCapable === true ||
  row?.capable === true ||
  row?.isFobOriginLocationRow === true;

export const rowHasAlphaPricingFilled = (row, usesAlphaField) => {
  if (usesAlphaField) return isPricingValueFilled(row?.alpha);
  return (
    isPricingValueFilled(row?.dyed) ||
    isPricingValueFilled(row?.undyed) ||
    isPricingValueFilled(row?.unitBbl) ||
    isPricingValueFilled(row?.cbm)
  );
};

export const rowHasCapableAlphaOffer = (row, usesAlphaField) => {
  if (!isCapableActive(row) || hasCounterOffer(row)) return false;
  return rowHasAlphaPricingFilled(row, usesAlphaField);
};

export const getCapableRowsMissingAlpha = (rows, usesAlphaField) =>
  (Array.isArray(rows) ? rows : []).filter(
    row =>
      isCapableActive(row) &&
      !hasCounterOffer(row) &&
      !rowHasAlphaPricingFilled(row, usesAlphaField)
  );

export const rowHasCounterAlphaPricingFilled = (row, usesAlphaField) => {
  if (usesAlphaField) {
    return (
      isPricingValueFilled(row?.counterAlpha) ||
      isPricingValueFilled(row?.alpha2)
    );
  }
  return (
    isPricingValueFilled(row?.counterDyed) ||
    isPricingValueFilled(row?.counterUndyed) ||
    isPricingValueFilled(row?.dyed2) ||
    isPricingValueFilled(row?.undyed2)
  );
};

export const rowHasCounterAlphaOffer = (row, usesAlphaField) =>
  rowHasCounterAlphaPricingFilled(row, usesAlphaField);

export const getCounterRowsMissingAlpha = (rows, usesAlphaField) =>
  (Array.isArray(rows) ? rows : []).filter(
    row =>
      hasCounterOffer(row) &&
      !rowHasCounterAlphaPricingFilled(row, usesAlphaField)
  );

export const hasAnyCargoAlphaOffer = (rows, usesAlphaField) =>
  (Array.isArray(rows) ? rows : []).some(
    row =>
      rowHasCapableAlphaOffer(row, usesAlphaField) ||
      (hasCounterOffer(row) &&
        rowHasCounterAlphaPricingFilled(row, usesAlphaField))
  );

const fetchDeliveryDetailGroups = async ({ procurementId, termId, token }) => {
  const response = await fetch(
    `${api.API_URL}/procurement/get-delivery-detail?PageNumber=1&PageSize=500&procurementId=${procurementId}&quantityDeliveryTermId=${termId}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const result = await response.json();
  return result?.data?.data || result?.data?.Data || [];
};

export const fetchDeliveryRowsForOfferValidation = async ({
  procurementId,
  termIds,
  token,
  form,
}) => {
  const usesAlphaField = !usesDyedUndyedAlpha(form?.productNameId);
  if (!procurementId || !token) return { rows: [], usesAlphaField };
  const ids = (Array.isArray(termIds) ? termIds : []).filter(Boolean);
  if (!ids.length) return { rows: [], usesAlphaField };

  const isLpg = isLpgProductId(form?.productNameId);
  const allRows = [];

  for (const termId of ids) {
    const groups = await fetchDeliveryDetailGroups({
      procurementId,
      termId,
      token,
    });
    let items = groups.flatMap(item =>
      getListData(item).map(details => mapDeliveryDetailRow(item, details))
    );

    try {
      items = await enrichDeliveryItemsWithFobLocations({
        quantityDeliveryTermId: termId,
        token,
        items,
      });
    } catch {
      void 0;
    }

    items = expandFobOriginCapableRows(items);

    if (isLpg) {
      const cargo = { id: termId };
      const requiresCargoRatio = requiresLpgCargoRatioForCargo(form, cargo);
      try {
        items = await enrichLpgDeliveryItemsWithCargoRatio({
          procurementId,
          quantityDeliveryTermId: termId,
          items,
          skipCargoRatio: !requiresCargoRatio,
        });
      } catch {
        void 0;
      }
    }

    try {
      items = await enrichDeliveryItemsWithCounterOffers({
        quantityDeliveryTermId: termId,
        token,
        items,
        requireIncotermCounter: isLpg,
      });
    } catch {
      void 0;
    }

    allRows.push(...items);
  }

  return { rows: allRows, usesAlphaField };
};

export const validateCargoAlphaBeforeSubmit = async ({
  procurementId,
  termIds,
  token,
  form,
}) => {
  const { rows, usesAlphaField } = await fetchDeliveryRowsForOfferValidation({
    procurementId,
    termIds,
    token,
    form,
  });

  if (!rows.length) {
    return { ok: false, message: CARGO_ALPHA_REQUIRED_MESSAGE };
  }

  if (getCapableRowsMissingAlpha(rows, usesAlphaField).length) {
    return { ok: false, message: CARGO_CAPABLE_ALPHA_REQUIRED_MESSAGE };
  }

  if (getCounterRowsMissingAlpha(rows, usesAlphaField).length) {
    return { ok: false, message: CARGO_COUNTER_ALPHA_REQUIRED_MESSAGE };
  }

  if (hasAnyCargoAlphaOffer(rows, usesAlphaField)) {
    return { ok: true, message: null };
  }

  return { ok: false, message: CARGO_ALPHA_REQUIRED_MESSAGE };
};
